import React from 'react'
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Delete } from '@mui/icons-material'
import { useDelete, useList, useNotification } from '@refinedev/core'
import { LoadingButton } from '@mui/lab'

interface UserOrganisation {
  user_uuid: string
  org_uuid: string
  role: string
  org_name: string
  charity_number?: string
}

interface UserOrganisationsListProps {
  userId: string
}

export const UserOrganisationsList: React.FC<UserOrganisationsListProps> = ({
  userId,
}) => {
  const { open } = useNotification()

  const { data, isLoading, isError, refetch } = useList<UserOrganisation>({
    resource: 'user-organisations',
    meta: {
      user_uuid: userId,
    },
  })

  const { mutate: deleteUserOrg, isLoading: isDeleting } = useDelete()

  const handleRemoveFromOrg = (orgUuid: string, orgName: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to remove this user from "${orgName}"?`
    )

    if (confirmed) {
      deleteUserOrg(
        {
          resource: 'users-orgs',
          id: `${orgUuid}/${userId}`,
        },
        {
          onSuccess: () => {
            open?.({
              type: 'success',
              message: `User successfully removed from ${orgName}`,
            })
            refetch()
          },
          onError: (error) => {
            open?.({
              type: 'error',
              message: `Failed to remove user from ${orgName}: ${error.message}`,
            })
          },
        }
      )
    }
  }

  if (isLoading) {
    return (
      <Box>
        <Typography variant="body2">Loading organisations...</Typography>
      </Box>
    )
  }

  if (isError) {
    return (
      <Box>
        <Typography variant="body2" color="error">
          Failed to load organisations
        </Typography>
      </Box>
    )
  }

  const organisations = data?.data || []

  if (organisations.length === 0) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          This user is not associated with any organisations.
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <TableContainer component={Paper} sx={{ mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Organisation</TableCell>
              <TableCell>Charity Number</TableCell>
              <TableCell>Role</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {organisations.map((org) => (
              <TableRow key={org.org_uuid}>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {org.org_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {org.charity_number || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{ textTransform: 'capitalize' }}
                  >
                    {org.role}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <LoadingButton
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    loading={isDeleting}
                    onClick={() =>
                      handleRemoveFromOrg(org.org_uuid, org.org_name)
                    }
                  >
                    Remove
                  </LoadingButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
